/** API module_name values from getProfile → route prefixes */
export const MODULES = {
  DASHBOARD: "Dashboard",
  DRIVER: "Driver",
  ACCOUNTANT: "Accountant Management",
  FUEL_OPERATOR: "Fuel Operator Management",
  FUEL_HISTORY: "Fuel History",
  FUEL_PUMP: "Fuel Pump Management",
  SALARY_AWARDS: "Salary & Award",
  MY_REQUEST: "My Request",
  REPORTS: "Reports",
  CMS: "CMS Management",
  MASTER: "Master",
  TRIP_HISTORY: "Trip History",
  SUB_ADMIN: "Sub Admin Management",
};

const PATH_MODULE_ENTRIES = [
  ["/dashboard", MODULES.DASHBOARD],
  ["/driver-management", MODULES.DRIVER],
  ["/accountant-management", MODULES.ACCOUNTANT],
  ["/fuel-operator-management", MODULES.FUEL_OPERATOR],
  ["/fuel-filled-history", MODULES.FUEL_HISTORY],
  ["/fuel-pump-management", MODULES.FUEL_PUMP],
  ["/trip-history", MODULES.TRIP_HISTORY],
  ["/sub-admin-management", MODULES.SUB_ADMIN],
  ["/salary-awards", MODULES.SALARY_AWARDS],
  ["/my-requests", MODULES.MY_REQUEST],
  ["/reports", MODULES.REPORTS],
  ["/content-management-system", MODULES.CMS],
  ["/company-master", MODULES.MASTER],
  ["/vehicle-master", MODULES.MASTER],
  ["/route-master", MODULES.MASTER],
  ["/location-master", MODULES.MASTER],
  ["/fuel-pump-master", MODULES.MASTER],
  ["/award-master", MODULES.MASTER],
].sort((a, b) => b[0].length - a[0].length);

export const normalizeModuleName = (name) =>
  String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

export const privilegeKeyFromName = (name) => {
  const n = (name || "").toString().trim().toLowerCase();
  if (n === "add") return "add";
  if (n === "edit") return "edit";
  if (n === "view") return "view";
  if (n === "delete") return "delete";
  return null;
};

/** permission[] from getProfile (supports alternate field names) */
export const getProfilePermissions = (profile) => {
  const list = profile?.permission ?? profile?.permissions ?? [];
  return Array.isArray(list) ? list : [];
};

/** Flat permission[] from getProfile → map keyed by normalized module name */
export const buildPermissionsMap = (permissions = []) => {
  const map = {};
  const list = Array.isArray(permissions) ? permissions : [];
  if (!list.length) return map;

  for (const entry of list) {
    const modName =
      entry?.module_id?.module_name ?? entry?.module_name ?? "";
    const priv =
      entry?.privilege_type_id?.privilege_type ??
      entry?.privilege_type ??
      "";
    const key = normalizeModuleName(modName);
    if (!key) continue;
    if (!map[key]) {
      map[key] = { add: false, edit: false, view: false, delete: false };
    }
    const pKey = privilegeKeyFromName(priv);
    if (pKey) map[key][pKey] = true;
  }
  return map;
};

export const getRoleName = (profile) =>
  profile?.role_id?.name ?? profile?.role?.name ?? profile?.role ?? "";

export const isSubAdminUser = (profile) => {
  const role = normalizeModuleName(getRoleName(profile));
  return role === "subadmin" || role === "sub_admin";
};

export const isGlobalAdminUser = (profile) => profile?.is_global_admin === true;

/** Profile carries an explicit module permission list from getProfile */
export const shouldUsePermissionList = (profile) =>
  getProfilePermissions(profile).length > 0;

/** Nav items keyed by API module_name (sidebar only shows these when user has View) */
export const NAV_ITEMS_BY_MODULE = [
  { moduleName: MODULES.DASHBOARD, path: "/dashboard" },
  { moduleName: MODULES.DRIVER, path: "/driver-management" },
  { moduleName: MODULES.ACCOUNTANT, path: "/accountant-management" },
  { moduleName: MODULES.FUEL_OPERATOR, path: "/fuel-operator-management" },
  { moduleName: MODULES.FUEL_HISTORY, path: "/fuel-filled-history" },
  { moduleName: MODULES.TRIP_HISTORY, path: "/trip-history" },
  { moduleName: MODULES.FUEL_PUMP, path: "/fuel-pump-management" },
  { moduleName: MODULES.SUB_ADMIN, path: "/sub-admin-management" },
  { moduleName: MODULES.SALARY_AWARDS, path: "/salary-awards" },
  { moduleName: MODULES.MY_REQUEST, path: "/my-requests" },
  { moduleName: MODULES.REPORTS, path: "/reports" },
  { moduleName: MODULES.CMS, path: "/content-management-system" },
];

export const isNavPathAllowed = (path, permissionsMap, profile) => {
  const item = NAV_ITEMS_BY_MODULE.find((n) => n.path === path);
  if (!item) {
    if (path.startsWith("/company-master") || path.startsWith("/vehicle-master") ||
        path.startsWith("/route-master") || path.startsWith("/location-master") ||
        path.startsWith("/fuel-pump-master") || path.startsWith("/award-master")) {
      if (path.startsWith("/location-master")) return isGlobalAdminUser(profile);
      return canViewModule(MODULES.MASTER, permissionsMap, profile);
    }
    return hasFullAccess(profile);
  }
  if (item.moduleName === MODULES.SUB_ADMIN && isSubAdminUser(profile)) return false;
  return canViewModule(item.moduleName, permissionsMap, profile);
};

/** Full access only for global admin or roles without a permission list */
export const hasFullAccess = (profile) => {
  if (!profile) return false;
  if (profile.is_global_admin === true) return true;
  if (shouldUsePermissionList(profile)) return false;
  return !isSubAdminUser(profile);
};

/** Module appears anywhere in the profile permission[] */
export const isModuleInPermissions = (moduleName, permissionsMap) => {
  const key = normalizeModuleName(moduleName);
  return Boolean(permissionsMap?.[key]);
};

export const canModule = (moduleName, action, permissionsMap, profile) => {
  if (hasFullAccess(profile)) return true;
  if (!isModuleInPermissions(moduleName, permissionsMap)) return false;
  const key = normalizeModuleName(moduleName);
  return Boolean(permissionsMap[key]?.[action]);
};

export const getModuleForPath = (pathname) => {
  const path = (pathname || "").split("?")[0].toLowerCase();
  for (const [prefix, module] of PATH_MODULE_ENTRIES) {
    if (path === prefix || path.startsWith(`${prefix}/`)) return module;
  }
  return null;
};

export const getActionForPath = (pathname) => {
  const p = (pathname || "").toLowerCase();
  if (
    p.includes("/add-new") ||
    p.includes("/create-") ||
    p.includes("/add-route")
  ) {
    return "add";
  }
  if (p.includes("/edit")) return "edit";
  return "view";
};

export const canAccessPath = (
  pathname,
  { permissionsMap, profile, action } = {}
) => {
  const path = (pathname || "").split("?")[0].toLowerCase();

  if (path.startsWith("/location-master") && !isGlobalAdminUser(profile)) {
    return false;
  }

  if (hasFullAccess(profile)) return true;

  if (isSubAdminUser(profile) && path.startsWith("/sub-admin-management")) {
    return false;
  }

  const module = getModuleForPath(pathname);
  if (!module) return false;

  const requiredAction = action ?? getActionForPath(pathname);

  // Listing/detail pages (view action) are reachable with ANY privilege on the
  // module, so a user with only edit/add/delete can still open the list to act.
  if (requiredAction === "view") {
    return isModuleInPermissions(module, permissionsMap);
  }

  return canModule(module, requiredAction, permissionsMap, profile);
};

/**
 * Sidebar / list pages: module is visible when the user has ANY privilege
 * (view, add, edit or delete) on it. Individual action buttons remain gated
 * by their specific privilege via PermissionGate.
 */
export const canViewModule = (moduleName, permissionsMap, profile) => {
  if (hasFullAccess(profile)) return true;
  return isModuleInPermissions(moduleName, permissionsMap);
};

/** Export is allowed for any privilege on the module (view, edit, add, or delete). */
export const canExportModule = (moduleName, permissionsMap, profile) =>
  canViewModule(moduleName, permissionsMap, profile);

/** First sidebar path the user may open (for redirects) */
export const getDefaultAllowedPath = (permissionsMap, profile) => {
  if (hasFullAccess(profile)) return "/dashboard";
  const order = [
    ["/dashboard", MODULES.DASHBOARD],
    ["/driver-management", MODULES.DRIVER],
    ["/accountant-management", MODULES.ACCOUNTANT],
    ["/fuel-operator-management", MODULES.FUEL_OPERATOR],
    ["/fuel-filled-history", MODULES.FUEL_HISTORY],
    ["/trip-history", MODULES.TRIP_HISTORY],
    ["/fuel-pump-management", MODULES.FUEL_PUMP],
    ["/salary-awards", MODULES.SALARY_AWARDS],
    ["/my-requests", MODULES.MY_REQUEST],
    ["/reports", MODULES.REPORTS],
    ["/content-management-system", MODULES.CMS],
    ["/company-master", MODULES.MASTER],
  ];
  for (const [path, mod] of order) {
    if (canViewModule(mod, permissionsMap, profile)) return path;
  }
  return "/dashboard";
};
